import { create } from 'zustand';

type Theme = 'light' | 'dark';

interface ThemeState {
  theme: Theme;
  isDark: boolean;
  toggleTheme: () => void;
  setLightTheme: () => void;
  setDarkTheme: () => void;
}

const getInitialTheme = (): Theme => {
  // Сначала проверяем основную тему
  const saved = localStorage.getItem('theme');
  if (saved && (saved === 'light' || saved === 'dark')) {
    return saved as Theme;
  }

  // Затем проверяем ресторанную тему для совместимости
  const restaurantSaved = localStorage.getItem('restaurant-theme');
  if (restaurantSaved && (restaurantSaved === 'light' || restaurantSaved === 'dark')) {
    return restaurantSaved as Theme;
  }

  // Проверяем системные настройки
  if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }

  return 'dark'; // По умолчанию темная тема
};

const applyTheme = (theme: Theme) => {
  const isDark = theme === 'dark';

  if (isDark) {
    document.documentElement.setAttribute('data-theme', 'dark');
    document.documentElement.setAttribute('data-restaurant-theme', 'dark');
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.removeAttribute('data-theme');
    document.documentElement.setAttribute('data-restaurant-theme', 'light');
    document.documentElement.classList.remove('dark');
  }
};

export const useThemeStore = create<ThemeState>((set) => {
  const initialTheme = getInitialTheme();
  applyTheme(initialTheme);

  return {
    theme: initialTheme,
    isDark: initialTheme === 'dark',

    toggleTheme: () => set((state) => {
      const newTheme: Theme = state.theme === 'light' ? 'dark' : 'light';
      localStorage.setItem('theme', newTheme);
      localStorage.setItem('restaurant-theme', newTheme);
      applyTheme(newTheme);
      return { theme: newTheme, isDark: newTheme === 'dark' };
    }),

    setLightTheme: () => set(() => {
      localStorage.setItem('theme', 'light');
      localStorage.setItem('restaurant-theme', 'light');
      applyTheme('light');
      return { theme: 'light', isDark: false };
    }),

    setDarkTheme: () => set(() => {
      localStorage.setItem('theme', 'dark');
      localStorage.setItem('restaurant-theme', 'dark');
      applyTheme('dark');
      return { theme: 'dark', isDark: true };
    }),
  };
});
